import { Button, EmptyState, Logo, Spinner, Wordmark } from "@qn/ui";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText } from "lucide-react";
import { useParams } from "react-router-dom";
import { Preview } from "../components/ArtifactPanel";
import { api } from "../lib/api";
import {
  type ArtifactType,
  downloadArtifact,
  isOffice,
  OFFICE_EXT,
  printArtifact,
} from "../lib/artifacts";
import { useT } from "../lib/i18n";

/** Public read-only view of a shared artifact (no sign-in). */
export function SharedPage() {
  const t = useT();
  const { token } = useParams();
  const q = useQuery({
    queryKey: ["shared", token],
    queryFn: () => api<{ title: string; type: ArtifactType; content: string }>(`/shared/${token}`),
    retry: 0,
  });
  return (
    <div className="flex h-dvh flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-[var(--line)] px-4">
        <Logo size={28} />
        <Wordmark by />
        <span className="ms-3 min-w-0 flex-1 truncate text-sm text-[var(--fg-muted)]" dir="auto">
          {q.data?.title}
        </span>
        {q.data && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void downloadArtifact(q.data.type, q.data.title, q.data.content)}
            >
              <Download className="size-3.5" />{" "}
              {isOffice(q.data.type) ? `.${OFFICE_EXT[q.data.type]}` : t("artifact.asSource")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void printArtifact(q.data.type, q.data.content)}
            >
              <FileText className="size-3.5" /> PDF
            </Button>
          </>
        )}
      </header>
      <div className="min-h-0 flex-1">
        {q.isLoading ? (
          <div className="grid h-full place-items-center">
            <Spinner />
          </div>
        ) : q.isError || !q.data ? (
          <EmptyState title={t("shared.gone")} hint={t("shared.goneHint")} />
        ) : (
          <Preview type={q.data.type} content={q.data.content} />
        )}
      </div>
      <footer className="shrink-0 border-t border-[var(--line)] px-4 py-2 text-center text-[11px] text-[var(--fg-faint)]">
        {t("shared.footer")} · © {new Date().getFullYear()} QN Ventures
      </footer>
    </div>
  );
}
